import { useCallback, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type ChallengeSeverity = 'low' | 'moderate' | 'elevated';

export interface ChallengeRiskDimension {
  id: string;
  label: string;
  severity: ChallengeSeverity;
  observation: string;
  data_point: string;
}

export interface ChallengeReport {
  symbol: string;
  generated_at: string;
  disclaimer: string;
  overall_risk_summary: string;
  risk_dimensions: ChallengeRiskDimension[];
  bear_case_scenario: string;
  questions_to_investigate: string[];
}

export interface ChallengeTokenInput {
  symbol: string;
  name: string;
  current_price: number | null;
  price_change_24h: number | null;
  price_change_7d: number | null;
  volume_24h: number | null;
  market_cap: number | null;
  toss_score?: number | null;
  toss_grade?: string | null;
  available_on_exchanges?: string[];
  coinGeckoId?: string | null;
}

export const useChallengeToken = () => {
  const [report, setReport] = useState<ChallengeReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cache = useRef<Map<string, ChallengeReport>>(new Map());

  const runChallenge = useCallback(async (input: ChallengeTokenInput) => {
    const cacheKey = (input.symbol || input.name || '').toUpperCase();
    const cachedReport = cacheKey ? cache.current.get(cacheKey) : undefined;
    if (cachedReport) {
      setReport(cachedReport);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setReport(null);

    try {
      const { coinGeckoId, ...tokenData } = input;
      const { data, error: fnError } = await supabase.functions.invoke('challenge-token', {
        body: { tokenData, coinGeckoId: coinGeckoId ?? null },
      });

      if (fnError) {
        let functionMessage = fnError.message || 'Function error';
        const context = (fnError as { context?: Response }).context;
        if (context) {
          try {
            const body = await context.clone().json() as { error?: string };
            if (body.error) functionMessage = body.error;
          } catch {
            // Keep the function client's message when the response is not JSON.
          }
        }
        throw new Error(functionMessage);
      }
      if (!data?.success) throw new Error(data?.error || 'Unknown error');

      const result = data.data as ChallengeReport;
      if (cacheKey) cache.current.set(cacheKey, result);
      setReport(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run challenge analysis');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setReport(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return { report, isLoading, error, runChallenge, reset };
};
